import { Random } from '@framework/Random';
import { Timestamp } from '@framework/Timestamp';
import type { WorkspaceJSON, WorkspaceJSONOnWrite, GroupId, UserId, ModelingFormat } from 'balus-common';
import { WorkspaceKey } from '@workspace/domain/key';
import { WorkspaceDescription } from './vo/WorkspaceDescription';
import { WorkspaceName } from './vo/WorkspaceName';
import { WorkspaceSetting } from './vo/WorkspaceSetting';
import { isEqual } from 'lodash-es';

export class Workspace {
  constructor(
    public readonly key: WorkspaceKey,
    public readonly name: WorkspaceName,
    public readonly description: WorkspaceDescription,
    public readonly groupId: GroupId,
    public readonly createdAt: Timestamp,
    public readonly updatedAt: Timestamp,
    public readonly setting: WorkspaceSetting,
    public readonly trashedAt: Timestamp | null,
    public readonly trashedBy: UserId | null,
    public readonly imageUrl: string | null,
    public readonly isPersonal: boolean
  ) {}

  static buildNew(
    name: string,
    groupId: string,
    timestamp: Timestamp,
    groupIndividualViewModelPermission: boolean,
    modelingFormat: ModelingFormat
  ): Workspace {
    const id = Random.generateRandomID(20);
    const key = WorkspaceKey.buildFromID(id);

    return new this(
      key,
      new WorkspaceName(name),
      new WorkspaceDescription(''),
      groupId,
      timestamp,
      timestamp,
      WorkspaceSetting.buildDefault(groupIndividualViewModelPermission, modelingFormat),
      null,
      null,
      null,
      false
    );
  }

  static buildNewPersonal(
    name: string,
    groupId: GroupId,
    userId: UserId,
    timestamp: Timestamp,
    modelingFormat: ModelingFormat
  ): Workspace {
    const key = WorkspaceKey.buildFromID(`${groupId}-${userId}`);

    return new this(
      key,
      new WorkspaceName(name),
      new WorkspaceDescription(''),
      groupId,
      timestamp,
      timestamp,
      WorkspaceSetting.buildForPersonalWorkspace(modelingFormat),
      null,
      null,
      null,
      true
    );
  }

  get id(): string {
    return this.key.workspaceID;
  }

  public get isPublicSpace(): boolean {
    return this.setting.isPublicSpace;
  }

  get isShared(): boolean {
    return !this.isPersonal;
  }

  dump(): WorkspaceJSONOnWrite {
    return {
      id: this.key.id,
      groupId: this.groupId,
      name: this.name.toString(),
      description: this.description.toString(),
      createdAt: this.createdAt.valueOf(),
      updatedAt: this.updatedAt.valueOf(),
      setting: this.setting.dump(),
      trashedAt: this.trashedAt?.valueOf() || null,
      trashedBy: this.trashedBy || null,
      imageUrl: this.imageUrl || null,
      isPersonal: this.isPersonal || false,
    };
  }

  static load(dump: WorkspaceJSON): Workspace {
    const workspaceKey = WorkspaceKey.buildFromID(dump.id);

    return new Workspace(
      workspaceKey,
      new WorkspaceName(dump.name),
      new WorkspaceDescription(dump.description || ''),
      dump.groupId,
      new Timestamp(dump.createdAt),
      new Timestamp(dump.updatedAt),
      WorkspaceSetting.load(dump.setting),
      dump.trashedAt ? new Timestamp(dump.trashedAt) : null,
      dump?.trashedBy || null,
      dump.imageUrl || null,
      dump.isPersonal || false
    );
  }

  static compare(a: Workspace, b: Workspace): number {
    return a.name.localeCompare(b.name);
  }

  public isEqual(other: Workspace): boolean {
    return isEqual(this.dump(), other.dump());
  }

  public matchQuery(query: string): boolean {
    const keywords = query.split(' ');
    return this.name.includesMulti(keywords);
  }
}

export const isWorkspace = (ws: Workspace | null): ws is Workspace => ws instanceof Workspace;
